((global) => {
  'use strict';
  const fail = (code, status) => Object.assign(new Error(code), { code, status });
  const expiry = value => typeof value === 'number' ? (value < 1e12 ? value * 1000 : value) : Date.parse(value);
  const pause = (ms, signal) => new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason || new DOMException('Aborted', 'AbortError'));
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, ms);
    const abort = () => { clearTimeout(timer); reject(signal.reason || new DOMException('Aborted', 'AbortError')); };
    signal?.addEventListener('abort', abort, { once: true });
  });
  function createClient(config, dependencies = {}) {
    const endpoint = new URL(config.endpoint);
    if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password || endpoint.search || endpoint.hash) throw fail('configuration');
    const base = endpoint.href.replace(/\/$/, '');
    const request = dependencies.fetch || global.fetch.bind(global);
    const wait = dependencies.wait || pause;
    const now = dependencies.now || Date.now;
    const uuid = dependencies.uuid || (() => global.crypto.randomUUID());
    const schedule = dependencies.setTimeout || global.setTimeout.bind(global);
    const unschedule = dependencies.clearTimeout || global.clearTimeout.bind(global);
    let session = null;
    let active = null;
    let generation = 0;
    const supportStorageKey = 'olea.support.visitor.v1';
    let visitorToken = null, conversationToken = null, supportState = null, supportRequest = null;
    let supportListener = null, supportRunning = false, supportTimer = null, supportFailures = 0, supportEpoch = 0;
    const checkpointKey = 'olea.chat.session.v2:' + String(global.Shopify?.theme?.id || 'published') + ':' + base;
    let storage; try { storage = dependencies.storage || global.sessionStorage; } catch {}
    let pendingTurn = null, completedTurn = null, view = null;
    const bounded = (value, max = 180000) => { try { const text = JSON.stringify(value, (key, item) => key === 'dataUrl' || (typeof item === 'string' && /^data:/i.test(item)) ? undefined : item); return text.length <= max ? JSON.parse(text) : null; } catch { return null; } };
    try {
      const saved = JSON.parse(storage?.getItem(checkpointKey) || 'null');
      if (saved?.v === 2 && saved.expiresAt > now() && saved.expiresAt <= now() + 7200000 && saved.endpoint === base) {
        if (typeof saved.session?.token === 'string' && saved.session.token.length <= 25000 && Number.isFinite(saved.session.expiresAt) && (saved.session.state == null || typeof saved.session.state === 'string' && saved.session.state.length <= 25000)) session = saved.session;
        if (typeof saved.visitorToken === 'string' && saved.visitorToken.length <= 4096) visitorToken = saved.visitorToken;
        if (typeof saved.conversationToken === 'string' && saved.conversationToken.length <= 4096) conversationToken = saved.conversationToken;
        const p = saved.pendingTurn;
        if (p && /^[a-z0-9-]{16,64}$/i.test(p.turnId || '') && Number.isFinite(p.expiresAt) && p.expiresAt > now() && p.expiresAt <= now() + 720000 && Number.isFinite(p.createdAt) && p.createdAt <= now() && typeof p.text === 'string' && p.text.length <= 1000 && (p.id == null || typeof p.id === 'string' && /^[a-z0-9_-]{1,160}$/i.test(p.id))) pendingTurn = { turnId:p.turnId, text:p.text, hasPhotos:p.hasPhotos === true, stopped:p.stopped === true, retrySubmitted:p.retrySubmitted === true, ownerToken:typeof p.ownerToken === 'string' && p.ownerToken.length <= 25000 ? p.ownerToken : null, history:Array.isArray(p.history)?p.history.filter(item=>['user','assistant'].includes(item?.role)&&typeof item.content==='string').slice(-4).map(item=>({role:item.role,content:item.content.slice(0,400)})):[], id:p.id || null, createdAt:p.createdAt, expiresAt:p.expiresAt };
        completedTurn = bounded(saved.completedTurn); view = bounded(saved.view, 120000);
      }
    } catch {}
    function save() {
      try { const value = bounded({ v: 2, endpoint: base, expiresAt: now() + 7200000, session, visitorToken, conversationToken, pendingTurn, completedTurn, view }, 250000); if (value) storage?.setItem(checkpointKey, JSON.stringify(value)); } catch {}
    }
    const recoverable = error => error?.code !== 'usage_exceeded' && (error?.code === 'detached' || error?.code === 'timeout' || error instanceof TypeError || error?.status >= 500 || ['backend_error', 'invalid_response'].includes(error?.code));
    try { const saved = JSON.parse(global.localStorage?.getItem(supportStorageKey) || 'null'); if (saved?.expiresAt > now() && typeof saved.token === 'string' && saved.token.length <= 4096) visitorToken = saved.token; } catch {}
    const supportVisible = () => global.document?.hidden !== true;
    const supportBody = () => ({ ...(visitorToken ? { visitorToken } : {}), ...(conversationToken ? { conversationToken } : {}) });
    function applySupport(data) {
      if (!data || typeof data.conversationId !== 'string' || !['ai', 'human', 'closed'].includes(data.mode) || typeof data.chatBanned !== 'boolean' || !Array.isArray(data.messages)) throw fail('invalid_support_state');
      if (supportState?.conversationId === data.conversationId && Number(data.version) < Number(supportState.version)) return supportState;
      if (typeof data.visitorToken === 'string' && data.visitorToken.length <= 4096) {
        visitorToken = data.visitorToken;
        try { global.localStorage?.setItem(supportStorageKey, JSON.stringify({ token: visitorToken, expiresAt: now() + 180 * 86400000 })); } catch {}
      }
      if (typeof data.conversationToken === 'string' && data.conversationToken.length <= 4096) conversationToken = data.conversationToken;
      const cursor = Math.max(supportState?.conversationId === data.conversationId ? Number(supportState.cursor) || 0 : 0, Number(data.cursor ?? data.nextAfterSeq) || 0);
      supportState = { ...data, cursor, messages: data.messages.filter(m => m && typeof m.id === 'string' && ['user', 'assistant', 'human', 'system'].includes(m.role) && typeof (m.text ?? m.content) === 'string').map(m => ({ ...m, text: m.text ?? m.content })) };
      if ((data.chatBanned || data.mode !== 'ai' || data.canSend === false) && (active || pendingTurn)) { const job = active || { ...pendingTurn, controller: new AbortController() }; active = null; void cancel(job); job.controller.abort(fail('support_state_changed')); completedTurn = null; }
      save();
      try { supportListener?.(supportState); } catch {}
      return supportState;
    }
    async function supportJson(path, body, signal = new AbortController().signal) {
      const current = await ensureSession(signal);
      try { return await json(path, { method: 'POST', body, token: current.token, signal }); }
      catch (error) { if (error.status !== 401 || signal.aborted) throw error; current.expiresAt = 0; const renewed = await ensureSession(signal); return json(path, { method: 'POST', body, token: renewed.token, signal }); }
    }
    async function ensureSupport({ refreshBanned = false } = {}) {
      if (supportState?.chatBanned && !refreshBanned) return supportState;
      if (supportRequest) return supportRequest;
      const epoch = supportEpoch;
      const request = (async () => {
        const analytics = !conversationToken ? global.OleaAnalytics?.context?.() : null;
        const data = await supportJson(conversationToken ? '/support/state' : '/support/open', { ...supportBody(), ...(conversationToken ? { afterSeq: Number(supportState?.cursor) || 0 } : {}), ...(analytics?.allowed ? { analytics } : {}) });
        if (epoch !== supportEpoch) throw fail('session_reset');
        let current = applySupport(data);
        for (let page = 1; current.hasMore === true && page < 3; page++) {
          const next = await supportJson('/support/state', { ...supportBody(), afterSeq: current.cursor });
          if (epoch !== supportEpoch) throw fail('session_reset');
          const before = current.cursor; current = applySupport(next);
          if (current.cursor <= before) break;
        }
        return current;
      })();
      supportRequest = request;
      try { return await request; } finally { if (supportRequest === request) supportRequest = null; }
    }
    function scheduleSupport() {
      unschedule(supportTimer); supportTimer = null;
      if (supportRunning && supportVisible() && !supportState?.chatBanned) supportTimer = schedule(pollSupport, Math.min(30000, 2000 * 2 ** Math.min(supportFailures, 4)));
    }
    async function pollSupport() {
      if (!supportRunning || !supportVisible() || supportState?.chatBanned) return;
      try { await ensureSupport(); supportFailures = 0; } catch { supportFailures++; }
      scheduleSupport();
    }
    async function connectSupport(listener) {
      supportListener = listener; supportRunning = true;
      const refreshBanned = supportState?.chatBanned === true;
      if (refreshBanned) conversationToken = null;
      try { const state = await ensureSupport({ refreshBanned }); supportFailures = 0; return state; }
      catch (error) { supportFailures++; throw error; }
      finally { scheduleSupport(); }
    }
    function pauseSupport() { supportRunning = false; unschedule(supportTimer); supportTimer = null; }
    async function sendSupport({ text, operationId = uuid() }) {
      const state = await ensureSupport();
      if (state.chatBanned) throw fail('chat_banned');
      if (state.mode === 'closed' || state.canSend === false) throw fail('chat_closed');
      if (state.mode === 'ai') return { state, aiAllowed: true };
      const epoch = supportEpoch;
      let result;
      try { result = await supportJson('/support/messages', { ...supportBody(), operationId, text: String(text).slice(0, 1000) }); }
      catch (error) { if (error.code !== 'ai_active') throw error; const current = await ensureSupport(); if (current.mode === 'ai' && !current.chatBanned && current.canSend !== false) return { state: current, aiAllowed: true }; throw error; }
      if (epoch !== supportEpoch) throw fail('session_reset');
      if (!result.state) throw fail('invalid_support_state');
      return { state: applySupport(result.state), aiAllowed: result.aiAllowed === true };
    }
    const supportVisibility = () => { if (!supportVisible()) { unschedule(supportTimer); supportTimer = null; } else if (supportRunning) void pollSupport(); };
    global.document?.addEventListener?.('visibilitychange', supportVisibility);
    async function json(path, { method = 'GET', body, token, signal, keepalive = false } = {}) {
      if (signal?.aborted) throw signal.reason || new DOMException('Aborted', 'AbortError');
      const controller = new AbortController();
      let detachedBackground = false, rejectLocal;
      const abort = () => { if (keepalive && signal.reason?.code === 'detached') { detachedBackground = true; rejectLocal?.(signal.reason); } else controller.abort(signal.reason || new DOMException('Aborted', 'AbortError')); };
      signal?.addEventListener('abort', abort, { once: true });
      let cancelled;
      const cancellation = new Promise((resolve, reject) => {
        cancelled = () => reject(controller.signal.reason || new DOMException('Aborted', 'AbortError'));
        rejectLocal = reject;
        controller.signal.addEventListener('abort', cancelled, { once: true });
      });
      const timer = schedule(() => controller.abort(fail('timeout')), 15000);
      try {
        const network = (async () => {
            const response = await request(base + path, { method, credentials: 'omit', mode: 'cors', signal: controller.signal, keepalive,
              headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
              ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
            let data;
            try { data = await response.json(); } catch { throw fail('invalid_response', response.status); }
            if (controller.signal.aborted) throw controller.signal.reason || new DOMException('Aborted', 'AbortError');
            if (!response.ok) throw fail(data?.error === 'usage_exceeded' || data?.code === 'usage_exceeded' ? 'usage_exceeded' : data?.error?.code || 'backend_error', response.status);
            if (!data || typeof data !== 'object') throw fail('invalid_response', response.status);
            return data;
          })();
        network.then(() => { if (detachedBackground) unschedule(timer); }, () => { if (detachedBackground) unschedule(timer); });
        return await Promise.race([network, cancellation]);
      } finally {
        if (!detachedBackground) unschedule(timer);
        signal?.removeEventListener('abort', abort);
        controller.signal.removeEventListener('abort', cancelled);
      }
    }
    async function ensureSession(signal) {
      const language = config.language.toUpperCase().replace(/-/g, '_');
      if (session && session.expiresAt > now() && session.country === config.country && session.language === language) return session;
      const previous = session;
      const version = generation;
      const data = await json('/session', { method: 'POST', signal, body: { country: config.country, language: config.language,
        ...(previous ? { previousToken: previous.token, state: previous.state } : {}) } });
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      if (generation !== version) throw fail('session_reset');
      const expiresAt = Math.min(expiry(data.expiresAt), now() + 7200000);
      if (typeof data.token !== 'string' || !data.token || !Number.isFinite(expiresAt) || expiresAt <= now()) throw fail('invalid_session');
      if (data.country !== undefined && data.country !== config.country) throw fail('invalid_market');
      if (data.language !== undefined && data.language !== config.language.toUpperCase().replace(/-/g, '_')) throw fail('invalid_language');
      if (previous?.state && (typeof data.signedState !== 'string' || !data.signedState)) throw fail('session_restore_required');
      if (pendingTurn?.ownerToken && pendingTurn.ownerToken === previous?.token) pendingTurn.ownerToken = data.token;
      session = Object.assign(previous || {}, { token: data.token, expiresAt, country: config.country, language, state: previous?.state ? data.signedState : null });
      save();
      return session;
    }
    async function cancel(job = active) {
      if (!job || job.finished || job.cancelled) return;
      job.cancelled = true;
      if (pendingTurn?.turnId === job.turnId) { pendingTurn.stopped = true; save(); }
      const controller = new AbortController();
      const timer = schedule(() => controller.abort(fail('timeout')), 5000);
      try {
        if (!job.token) job.token = (await ensureSession(controller.signal)).token;
        if (!job.id && job.turnId) {
          for (let attempt = 0; attempt < 3 && !job.id; attempt++) {
            const found = await json(`/turns/${encodeURIComponent(job.turnId)}`, { token: job.token, signal: controller.signal });
            if (typeof found.jobId === 'string') job.id = found.jobId;
            else if (attempt < 2) await wait(500, controller.signal);
          }
        }
        if (job.id) await json(`/jobs/${encodeURIComponent(job.id)}/cancel`, { method: 'POST', token: job.token, body: {}, keepalive: true, signal: controller.signal });
      } catch {}
      finally { unschedule(timer); }
    }
    async function run({ text, images = [], history = [], turnId = uuid(), signal: callerSignal, onAccepted, restore = false } = {}) {
      if (active) throw fail('request_in_progress');
      if (!Array.isArray(images) || images.length > 2) throw fail('invalid_images');
      const safeImages = images.map(image => {
        if (!image || typeof image.dataUrl !== 'string' || image.dataUrl.length > 1500000 || !/^data:image\/(?:jpeg|png|webp);base64,[A-Za-z0-9+/]+=*$/.test(image.dataUrl)) throw fail('invalid_images');
        return { dataUrl: image.dataUrl, name: String(image.name || '').slice(0, 100), detail: 'low' };
      });
      const startGeneration = generation;
      if (!restore) { pendingTurn = { turnId, text: String(text).slice(0, 1000), history: history.filter(item => ['user','assistant'].includes(item?.role)).slice(-4).map(item => ({role:item.role,content:String(item.content).slice(0,400)})), hasPhotos: images.length > 0, ownerToken:session?.token || null, phase:'preparing', id:null, createdAt:now(), expiresAt:now()+600000 }; completedTurn = null; save(); }
      let support;
      try { support = !restore && supportState ? supportState : await ensureSupport(); }
      catch (error) { if (startGeneration !== generation) throw fail('detached'); throw error; }
      if (startGeneration !== generation) throw fail('detached');
      if (pendingTurn?.turnId === turnId && pendingTurn.stopped) throw fail('cancelled');
      if (active) throw fail('request_in_progress');
      if (support.chatBanned || support.mode !== 'ai' || support.canSend === false) { if (pendingTurn) { pendingTurn.stopped = true; save(); } throw fail(support.chatBanned ? 'chat_banned' : support.mode === 'closed' || support.canSend === false ? 'chat_closed' : 'human_active'); }
      const controller = new AbortController();
      const signal = callerSignal ? AbortSignal.any([callerSignal, controller.signal]) : controller.signal;
      const version = generation;
      const job = { id: restore ? pendingTurn?.id || null : null, turnId, token: null, finished: false, controller };
      active = job;
      const deadlineTimer = schedule(() => controller.abort(fail('timeout')), 300000);
      const onAbort = () => { if (signal.reason?.code !== 'detached') void cancel(job); };
      signal.addEventListener('abort', onAbort, { once: true });
      try {
        const current = await ensureSession(signal);
        job.token = current.token;
        const safeHistory = (restore ? pendingTurn?.history || [] : history).filter(item => ['user', 'assistant'].includes(item.role)).slice(-4).map(item => ({ role: item.role, content: String(item.content).slice(0, 400) }));
        const submit = () => {
          const body = { text: String(text).slice(0, 1000), history:safeHistory, state:current.state, turnId, images:safeImages, support:supportBody() };
          const bytes = typeof global.TextEncoder === 'function' ? new global.TextEncoder().encode(JSON.stringify(body)).byteLength : JSON.stringify(body).length * 3;
          return json('/chat', { method:'POST', signal, token:current.token, body, keepalive:safeImages.length === 0 && bytes < 60000 });
        };
        if (!restore) { pendingTurn.ownerToken = current.token; pendingTurn.phase = 'submitted'; save(); }
        let accepted;
        if (restore) {
          if (pendingTurn?.stopped) { void cancel(job); throw fail('cancelled'); }
          if (job.id) accepted = { jobId: job.id, expiresAt: pendingTurn.expiresAt };
          else {
            for (let attempt = 0; attempt < 5; attempt++) {
              accepted = await json(`/turns/${encodeURIComponent(turnId)}`, { token: current.token, signal });
              if (accepted.status !== 'not_found') break;
              if (attempt < 4 && now() - pendingTurn.createdAt < 30000) await wait(1000, signal); else break;
            }
            if (accepted.status === 'not_found') {
              if (!pendingTurn.hasPhotos && !pendingTurn.retrySubmitted && now() - pendingTurn.createdAt < 30000 && pendingTurn.ownerToken === current.token) { pendingTurn.retrySubmitted = true; pendingTurn.phase = 'submitted'; save(); accepted = await submit(); }
              else throw fail('turn_not_found');
            }
          }
        } else accepted = await submit();
        if (accepted.status === 'manual' && accepted.support) { job.finished = true; applySupport(accepted.support); throw fail('human_active'); }
        if (typeof accepted.jobId !== 'string' || !accepted.jobId || accepted.jobId.length > 160) throw fail('invalid_job');
        job.id = accepted.jobId;
        if (pendingTurn?.turnId === turnId) { pendingTurn.id = job.id; if (Number.isFinite(expiry(accepted.expiresAt))) pendingTurn.expiresAt = expiry(accepted.expiresAt); save(); }
        if (signal.aborted) { if (signal.reason?.code !== 'detached') void cancel(job); throw signal.reason || new DOMException('Aborted', 'AbortError'); }
        try { onAccepted?.(job.id); } catch {}
        const deadline = Math.min(Number.isFinite(expiry(accepted.expiresAt)) ? expiry(accepted.expiresAt) : now() + 300000, now() + 300000);
        let interval = Math.max(250, Math.min(5000, Number(accepted.pollAfterMs) || 1500));
        let pollFailures = 0;
        while (now() < deadline) {
          await wait(Math.min(interval, Math.max(0, deadline - now())), signal);
          if (signal.aborted) throw signal.reason || new DOMException('Aborted', 'AbortError');
          if (now() >= deadline) break;
          let data;
          try { data = await json(`/jobs/${encodeURIComponent(job.id)}`, { token: job.token, signal }); }
          catch (error) {
            if (error.status === 401 && !job.renewed) {
              job.renewed = true;
              current.expiresAt = 0;
              job.token = (await ensureSession(signal)).token;
              data = await json(`/jobs/${encodeURIComponent(job.id)}`, { token: job.token, signal });
            } else if (!signal.aborted && (error instanceof TypeError || error.code === 'timeout' || error.status >= 500 || error.status === 429) && pollFailures < 3) {
              pollFailures += 1;
              interval = Math.min(5000, 500 * 2 ** pollFailures);
              continue;
            } else throw error;
          }
          pollFailures = 0;
          if (data.support) applySupport(data.support);
          if (data.status === 'complete') {
            if (typeof data.result?.reply !== 'string' || typeof data.result?.signedState !== 'string') throw fail('invalid_result');
            if (signal.aborted || version !== generation) throw new DOMException('Aborted', 'AbortError');
            job.finished = true;
            current.state = data.result.signedState;
            const publicResult = Object.fromEntries(['reply', 'productIds', 'productDetails', 'comparisonIds', 'suggestions', 'action', 'navigationActions', 'commerceNotice', 'api'].filter(key => data.result[key] !== undefined).map(key => [key, data.result[key]]));
            completedTurn = { turnId, result: publicResult }; pendingTurn = null; save();
            return data.result;
          }
          if (data.status === 'failed' || data.status === 'cancelled') { job.finished = true; const error = fail(data.error?.code || data.status); error.api = data.error?.api; throw error; }
          if (!['pending', 'running'].includes(data.status)) throw fail('invalid_status');
          interval = Math.max(250, Math.min(5000, Number(data.pollAfterMs) || interval));
        }
        void cancel(job);
        throw fail('timeout');
      } catch (error) {
        if (signal.aborted && signal.reason && (error?.name === 'AbortError' || error?.code == null || typeof error?.code === 'number')) error = signal.reason;
        if (pendingTurn?.turnId === turnId && !pendingTurn.stopped && (job.finished || !recoverable(error))) { pendingTurn = null; save(); }
        if (signal.aborted && signal.reason?.code === 'timeout') throw signal.reason;
        if (signal.aborted && signal.reason?.code === 'timeout') void cancel(job);
        if (error.status === 401 && !job.id && session) session.expiresAt = 0;
        throw error;
      } finally {
        unschedule(deadlineTimer);
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted && signal.reason?.code !== 'detached') void cancel(job);
        if (active === job) active = null;
      }
    }
    const chat = options => run(options);
    async function resume(options = {}) {
      if (completedTurn?.result) { const state = await ensureSupport(); if (state.chatBanned || state.mode !== 'ai' || state.canSend === false) { completedTurn = null; save(); throw fail('support_state_changed'); } return completedTurn.result; }
      if (!pendingTurn) return null;
      if (pendingTurn.stopped) { void cancel({ ...pendingTurn, controller: new AbortController() }); return null; }
      return run({ ...options, text: pendingTurn.text, turnId: pendingTurn.turnId, restore: true });
    }
    return {
      chat, resume,
      warmup() { return ensureSupport(); },
      getCheckpoint() { return bounded({ pendingTurn, completedTurn, view }); },
      saveView(value) { view = bounded(value, 120000); save(); },
      acknowledgeResult() { completedTurn = null; save(); },
      connectSupport, pauseSupport, sendSupport, ensureSupport,
      getSupportState() { return supportState; },
      stop() { const job = active || (pendingTurn ? { ...pendingTurn, controller: new AbortController() } : null); active = null; if (pendingTurn) pendingTurn.stopped = true; save(); if (job) { void cancel(job); job.controller.abort(); } },
      reset() { this.stop(); generation++; supportEpoch++; pauseSupport(); supportRequest = null; conversationToken = null; supportState = null; session = null; pendingTurn = completedTurn = view = null; save(); },
      detach() { save(); supportEpoch++; generation++; supportListener = null; supportRequest = null; pauseSupport(); global.document?.removeEventListener?.('visibilitychange', supportVisibility); const job = active; active = null; if (job) job.controller.abort(fail('detached')); },
      destroy() { this.detach(); }
    };
  }
  const api = { createClient };
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (global) global.OleaAssistantTransport = api;
})(typeof window === 'undefined' ? globalThis : window);

